function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { Ribbon, Card, Empty, Search, Pending } from "../../components/analytics-kit";
import { fetchSales, comparisonQs, deltaOfPaced } from "../sales/territory-common";
import { count, money, displayTitle, catLabel, Thumb } from "./px-common";

/*
 * Product Explorer → Series tab (mockup productsflow/Series.dc.html).
 * GET /v1/sales/families/{range} — rows {family, skus, orders, quantity,
 * totalAmount, prevTotalAmount} plus, once the extension deploys, topTitles,
 * category, imageLink and dealers. Every extended field degrades gracefully.
 */

const PAGE = 50;
const N = v => Number(v) || 0;
function titlesOf(r) {
  const t = r.topTitles;
  const list = Array.isArray(t) ? t : t ? String(t).split(/\s*[|,;]\s*/) : [];
  const seen = {};
  return list.map(x => displayTitle(x)).filter(x => x && !seen[x.toLowerCase()] && (seen[x.toLowerCase()] = true)).slice(0, 2);
}
export default class SeriesList extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      rows: null,
      error: false,
      q: "",
      shown: PAGE
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "_seq", 0);
    _defineProperty(this, "open", r => r.family && this.props.history.push(`/Products/${this.props.range}/family/${encodeURIComponent(r.family)}`));
  }
  componentDidMount() {
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  componentDidUpdate(prev) {
    if (prev.range !== this.props.range || prev.statusKey !== this.props.statusKey || prev.acctKv !== this.props.acctKv || prev.comp !== this.props.comp) {
      this.setState({
        rows: null,
        error: false,
        shown: PAGE
      }, () => this.load());
    }
  }
  async load() {
    const seq = ++this._seq;
    const {
      range,
      statusKey,
      acctKv,
      comp
    } = this.props;
    const kv = [];
    if (statusKey) kv.push(`status=${encodeURIComponent(statusKey)}`);
    const cq = comparisonQs(range, false, comp).replace(/^&/, "");
    if (cq) kv.push(cq);
    if (acctKv) kv.push(acctKv);
    try {
      const rows = await fetchSales(`/sales/families/${range}${kv.length ? "?" + kv.join("&") : ""}`);
      if (!this._alive || seq !== this._seq) return;
      const list = (Array.isArray(rows) ? rows : []).filter(r => r && r.family).sort((a, b) => N(b.totalAmount) - N(a.totalAmount));
      this.setState({
        rows: list,
        error: false
      });
    } catch (e) {
      if (this._alive && seq === this._seq) this.setState({
        error: true
      });
    }
  }
  vsLabel() {
    return this.props.comp === "prev_year" ? "vs last year" : "vs previous period";
  }
  renderRibbon(rows, rev) {
    const range = this.props.range;
    const prev = rows.reduce((a, r) => a + N(r.prevTotalAmount), 0);
    const d = prev > 0 ? deltaOfPaced(rev, prev, range) : null;
    const skus = rows.reduce((a, r) => a + N(r.skus), 0);
    const big = rows[0];
    const top10 = rows.slice(0, 10).reduce((a, r) => a + N(r.totalAmount), 0);
    const r0 = d ? Math.round(d.pct) : 0;
    return /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      cols: 4,
      tiles: [{
        l: "Series selling",
        v: count(rows.length),
        s: `with ${count(skus)} products`
      }, {
        l: "Series revenue",
        v: money(rev),
        s: d ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
          className: `px-chg ${r0 > 0 ? "up" : r0 < 0 ? "down" : "flat"}`
        }, r0 > 0 ? "+" : r0 < 0 ? "−" : "", Math.abs(r0), "%"), " ", this.vsLabel()) : ""
      }, {
        l: "Biggest series",
        v: big ? `Series ${big.family}` : "—",
        s: big ? `${money(big.totalAmount)}, ${count(big.skus)} products` : ""
      }, {
        l: "Top 10 series",
        v: rev > 0 ? `${Math.round(top10 / rev * 100)}%` : "—",
        s: "of series revenue"
      }]
    });
  }

  // Only when series revenue sits meaningfully below a figure shown elsewhere. Names the figure it
  // compares against: the Products tab's Revenue tile when that is larger, otherwise total sales.
  renderNote(rev) {
    const ref = this.props.productsRevenue || {};
    const gap = all => all && rev && all - rev >= Math.max(all * 0.02, 1);
    const products = N(ref.products),
      sales = N(ref.sales);
    let all = 0,
      where = "";
    if (gap(products)) {
      all = products;
      where = "on the Products tab";
    } else if (gap(sales)) {
      all = sales;
      where = products ? "in total sales on the Dashboard and Sales pages (the Products tab leaves them out too)" : "in total sales on the Dashboard and Sales pages";
    }
    if (!all) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: "px-note"
    }, /*#__PURE__*/React.createElement("span", {
      className: "px-dot",
      "aria-hidden": "true"
    }), "Series count only products that belong to a series. Items sold on their own (for example fabric by the yard, freight, samples and kits, ", money(all - rev), " this period) are left out, so this is less than the ", money(all), " ", where, ".");
  }
  renderRow(r, max, range) {
    const titles = titlesOf(r);
    const head = `Series ${r.family}${titles.length ? " · " + titles.join(", ") : ""}`;
    const n = N(r.skus);
    const sub = [`${count(n)} product${n === 1 ? "" : "s"}`, r.category ? `${n > 1 ? "mostly " : ""}${catLabel(r.category).toLowerCase()}` : null].filter(Boolean).join(" · ");
    const pct = max > 0 ? Math.max(1, Math.round(N(r.totalAmount) / max * 100)) : 0;
    const d = N(r.prevTotalAmount) > 0 ? deltaOfPaced(r.totalAmount, r.prevTotalAmount, range) : null;
    const p = d ? Math.round(d.pct) : null;
    return /*#__PURE__*/React.createElement("tr", {
      key: r.family,
      className: "clickable",
      onClick: () => this.open(r),
      title: `Open Series ${r.family}`
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "px-ident"
    }, /*#__PURE__*/React.createElement(Thumb, {
      src: r.imageLink
    }), /*#__PURE__*/React.createElement("span", {
      className: "px-ident-txt"
    }, /*#__PURE__*/React.createElement("span", {
      className: "px-name"
    }, head), /*#__PURE__*/React.createElement("span", {
      className: "px-sub"
    }, sub)))), /*#__PURE__*/React.createElement("td", {
      className: "px-share"
    }, /*#__PURE__*/React.createElement("span", {
      className: "px-bar"
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        width: `${pct}%`
      }
    }))), /*#__PURE__*/React.createElement("td", {
      className: "n strong"
    }, money(r.totalAmount)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, count(r.quantity)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, r.dealers != null ? count(r.dealers) : /*#__PURE__*/React.createElement("span", {
      className: "px-muted"
    }, "\u2014")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, p != null ? /*#__PURE__*/React.createElement("span", {
      className: `px-chg ${p > 0 ? "up" : p < 0 ? "down" : "flat"}`
    }, p > 0 ? "+" : p < 0 ? "−" : "", Math.abs(p), "%") : /*#__PURE__*/React.createElement("span", {
      className: "px-muted"
    }, "\u2014")));
  }
  render() {
    const {
      rows,
      error,
      q,
      shown
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Empty, null, "The series couldn't be loaded. Try again in a minute.");
    if (!rows) return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Ribbon, {
      loading: true,
      tiles: []
    }), /*#__PURE__*/React.createElement(Pending, {
      h: 360
    }));
    const rev = rows.reduce((a, r) => a + N(r.totalAmount), 0);
    const term = q.trim().toLowerCase();
    const list = term ? rows.filter(r => String(r.family).toLowerCase().includes(term) || titlesOf(r).join(" ").toLowerCase().includes(term)) : rows;
    const max = rows.length ? N(rows[0].totalAmount) : 0;
    return /*#__PURE__*/React.createElement(React.Fragment, null, this.renderRibbon(rows, rev), this.renderNote(rev), /*#__PURE__*/React.createElement(Card, {
      cls: "px-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "px-toolbar"
    }, /*#__PURE__*/React.createElement("span", {
      className: "px-card-title"
    }, /*#__PURE__*/React.createElement("b", null, "Series by revenue"), rows.some(r => r.topTitles) ? /*#__PURE__*/React.createElement("span", {
      className: "px-hint"
    }, "each series is named by its best sellers") : null), /*#__PURE__*/React.createElement("span", {
      className: "px-tools"
    }, /*#__PURE__*/React.createElement(Search, {
      value: q,
      onChange: v => this.setState({
        q: v,
        shown: PAGE
      }),
      placeholder: "Search series or product"
    }))), !rows.length ? /*#__PURE__*/React.createElement(Empty, null, "No series sold in this period.") : !list.length ? /*#__PURE__*/React.createElement(Empty, null, "No series match \"", q, "\".") : /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table px-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Series"), /*#__PURE__*/React.createElement("th", null, "Share of series revenue"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Units"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, this.props.dealerWord), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, this.vsLabel()))), /*#__PURE__*/React.createElement("tbody", null, list.slice(0, shown).map(r => this.renderRow(r, max, this.props.range))))), list.length > shown ? /*#__PURE__*/React.createElement("div", {
      className: "px-foot center"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "px-more",
      onClick: () => this.setState({
        shown: list.length
      })
    }, "Show all ", count(list.length), " series")) : null));
  }
}